import { Component, ElementRef, Input, NgZone, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { MorphPath } from './morph-path';

@Component({
    selector: 'svg-morph',
    templateUrl: './svg-morph.component.html',
    styleUrls: ['./svg-morph.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SvgMorphComponent implements OnInit {
    // Define max number of output paths available for morphing
    public svgOutputPaths = new Array(8);

    /**
    * Name of first SVG icon to be morphed.
    */
    public firstIcon: string | SafeHtml = "circle-solid";
    @Input('firstIcon') set setFirstIcon(icon: string | SafeHtml) {
        this.firstIcon = icon ? icon : 'circle-solid';
        this.initAnimation();
    }

    /**
    * Name of first SVG icon to be morphed.
    */
    public secondIcon: string | SafeHtml = "circle-solid";
    @Input('secondIcon') set setSecondIcon(icon: string | SafeHtml) {
        this.secondIcon = icon ? icon : 'circle-solid';
        this.initAnimation();
    }

    /**
     * Mark icon input format is html instead to load custom svg files instead of default icon names
     */
    @Input() htmlIconInputEnabled: boolean = false;

    /**
    * Animation time to morphing icons in ms
    */
    @Input() animationTime: number = 300;

    /**
    * Delay of morphing animation in ms
    */
    @Input() animationDelay: number = 0;

    @ViewChild('firstIconElement', { read: ElementRef }) firstIconElement: ElementRef<HTMLOrSVGImageElement>;
    @ViewChild('secondIconElement', { read: ElementRef }) secondIconElement: ElementRef<HTMLOrSVGImageElement>;
    @ViewChild('morphIconElement', { read: ElementRef }) morphIconElement: ElementRef<HTMLOrSVGImageElement>;

    private readonly EASE_IN_POWER = 1.65;

    private delayedTimer: any = null;
    private viewInitialized: boolean = false;

    constructor(private ngZone: NgZone) {

    }

    ngOnInit(): void {
    }

    ngAfterViewInit() {
        this.viewInitialized = true;
        this.initAnimation();
    }

    private initAnimation() {
        if(!this.viewInitialized || this.firstIcon == this.secondIcon) return;
        // console.log("Init animation: " + this.firstIcon + " -> " + this.secondIcon);

        // Run morphing animation outside of angular render
        this.ngZone.runOutsideAngular(() => {
            this.renderMorphingDelayed();
        });
    }

    /**
    * Start morphing animation with delay 
    */
    private async renderMorphingDelayed() {
        if(!this.firstIcon || !this.secondIcon) return;

        // Wait for dom to update (timeout is unfortunately needed as we dont know when the svg is loaded)
        await new Promise<void>((resolve) => setTimeout(() => resolve(), 50));

        // Get paths from svg elements in dom
        let firstSvg = this.firstIconElement.nativeElement;
        let secondSvg = this.secondIconElement.nativeElement;
        let morphedSvg = this.morphIconElement.nativeElement;

        // Render path pair list from first and second Svg
        let morphPaths = this.generateMorphPaths(firstSvg, secondSvg);
        // Init morphed SVG result with number of needed paths
        this.initMorphedResult(morphedSvg, morphPaths);

        // Clear old animation timer
        if(this.delayedTimer) clearTimeout(this.delayedTimer);
        // Start delayed animation
        this.delayedTimer = setTimeout(() => {
            // Start animation
            this.renderMorphing(morphPaths);
        }, this.animationDelay);
    }

    /**
     * Initialize the output/result SVG dom element with needed number of path elements
     */
    private initMorphedResult(morphedSvg: HTMLOrSVGImageElement, morphPaths: MorphPath[]) {
        // Get existing paths
        let outputPaths = morphedSvg.querySelectorAll('path');
        // Add missing paths to animate in
        for(let idx = 0; idx < morphPaths.length; idx++) {
            let morphPath = morphPaths[idx];
            // Save output path in morphPath object for convenience 
            morphPath.outputPath = outputPaths[idx];
            // Init the path
            morphPath.outputPath.setAttribute("d", morphPath.render(0));
            // Initialize color of path
            morphPath.outputPath.style.color = morphPath.pathColor;
            morphPath.outputPath.style.transition = `color ${this.animationTime}ms ease`;
        }
    }

    /**
     * Generate a list of path morphing pairs to be morphed together
     */
    private generateMorphPaths(firstSvg: HTMLOrSVGImageElement, secondSvg: HTMLOrSVGImageElement): MorphPath[] {
        let firstPaths = firstSvg.querySelectorAll('path');
        let secondPaths = secondSvg.querySelectorAll('path');
        // Find the number of path elements needed to morph the two
        let pathCount = Math.max(firstPaths.length, secondPaths.length);

        let pathList = [];

        for(let idx = 0; idx < pathCount; idx++) {
            let firstPath = (idx < firstPaths.length) ? firstPaths[idx] : null;
            let secondPath = (idx < secondPaths.length) ? secondPaths[idx] : null;

            pathList.push(new MorphPath(firstPath, secondPath));
        }
        // console.log("Number of paths to be morphed " + pathCount, pathList);
        return pathList;
    }

    /**
    * Render morphing animation which interpolates between first and second SVG path 
    * and draw it in the given target SVG element.  
    */
    private renderMorphing(morphPaths: MorphPath[]) {
        // Init animation variables
        let startTime = undefined;
        const morphTime = this.animationTime;

        // Define draw function requesting animation frame
        const draw = (time) => {
            // Init start time with time stamp from animation frame
            if(startTime == undefined) startTime = time;
            // Calculate elapsed time
            const elapsed = time - startTime;
            // Calculate time with exponential growth (ease-in timing function) 
            let t = 1 - Math.pow(1 - (elapsed / morphTime), this.EASE_IN_POWER);
            // Clamp at 1
            if(t > 1 || isNaN(t)) t = 1;
            // Update the path of the target SVG element
            for(let morph of morphPaths) {
                morph.outputPath.setAttribute("d", morph.render(t));
            }
            // Don't animate in SSR where request animation frame is not defined
            if(typeof requestAnimationFrame == 'undefined') return;
            // Request next frame if animation is not done
            if(t < 1) requestAnimationFrame(draw);
        }

        // Only render last frame in SSR
        if(typeof requestAnimationFrame == 'undefined') {
            draw(1);
            return;
        }
        // Start animation
        requestAnimationFrame(draw);
    }
}
